import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Anchor, Loader2, Download, ShieldAlert } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function EvidenceShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get(`/portal/shared/${token}`)
      .then(({ data }) => setData(data))
      .catch(() => setError(true));
  }, [token]);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#051329] px-5" data-testid="evidence-share-unavailable">
        <div className="text-center text-slate-300 max-w-sm">
          <ShieldAlert className="h-8 w-8 text-[#D4AF37] mx-auto mb-3" />
          <p className="text-lg text-white">This evidence link is unavailable.</p>
          <p className="text-sm mt-1">It may have expired or been revoked by the vessel owner.</p>
        </div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#051329]">
        <Loader2 className="h-6 w-6 animate-spin text-[#D4AF37]" />
      </div>
    );

  const p = data.package || {};
  const cov = p.coverage || {};

  const Section = ({ title, items, render, testid }) => (
    <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid={testid}>
      <div className="text-sm font-medium text-[#051329] mb-2">{title}</div>
      {!items || items.length === 0 ? (
        <p className="text-sm text-slate-400">None on record for this period.</p>
      ) : (
        <ul className="space-y-1 text-sm text-slate-700">{items.slice(0, 50).map((it, i) => <li key={i}>{render(it)}</li>)}</ul>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="evidence-share-page">
      <header className="bg-[#051329] text-white px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#D4AF37] tracking-[0.25em] text-xs">
          <Anchor className="h-4 w-4" /> HMC INSURANCE EVIDENCE
        </div>
        <a
          data-testid="evidence-share-download"
          href={`${BACKEND}/api/portal/shared/${token}/download`}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#051329] text-sm font-medium px-3 py-1.5 rounded-lg"
        >
          <Download className="h-4 w-4" /> PDF
        </a>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100">
          <h1 className="text-xl font-semibold text-[#051329]" data-testid="evidence-share-vessel">{p.vessel?.name}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {p.vessel?.make} {p.vessel?.model} {p.vessel?.year}
            {p.vessel?.berth_type === "private_dock_lift" ? " · Private dock & lift" : " · Marina slip"}
          </p>
          <p className="text-[11px] text-slate-400 mt-2 font-mono break-all" data-testid="evidence-share-hash">
            Package SHA-256: {data.content_hash}
          </p>
          <p className="text-[11px] text-slate-400">Generated {data.generated_at?.slice(0, 16).replace("T", " ")} · link expires {data.expires_at?.slice(0, 10)}</p>
        </div>

        <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="evidence-share-coverage">
          <div className="text-sm font-medium text-[#051329] mb-2">Service & coverage</div>
          <p className="text-sm text-slate-700">
            Scheduled {cov.scheduled_visits ?? 0} · completed {cov.completed_visits ?? 0} · overdue/missed {cov.overdue_or_missed_visits ?? 0} · completed inspections {cov.completed_inspections ?? 0} · coverage-gap observations {cov.coverage_gap_observations ?? 0}
          </p>
        </div>

        <Section testid="evidence-share-attention" title="ATTENTION observations"
          items={p.observations?.attention} render={(r) => `${r.component_key} — attention (${(r.at || "").slice(0, 10)})`} />
        <Section testid="evidence-share-urgent" title="URGENT observations"
          items={p.observations?.urgent} render={(r) => `${r.component_key} — urgent (${(r.at || "").slice(0, 10)})`} />
        <Section testid="evidence-share-authorizations" title="Owner authorization chronology"
          items={p.owner_authorizations} render={(a) => `${(a.decided_at || a.created_at || "").slice(0, 10)} — ${a.action_type} — ${a.status}`} />
        <Section testid="evidence-share-contractor" title="Contractor involvement (as recorded)"
          items={p.contractor_involvement} render={(c) => `${(c.at || "").slice(0, 10)} — ${c.contractor_reference || "—"}${c.owner_authorized ? " (owner-authorized)" : ""}`} />
        <Section testid="evidence-share-verification" title="Subsequent HMC observable verification"
          items={p.observable_verification} render={(v) => `${(v.at || "").slice(0, 10)} — observable verification recorded`} />

        {p.photos?.length > 0 && (
          <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="evidence-share-photos">
            <div className="text-sm font-medium text-[#051329] mb-2">Selected timestamped photographs</div>
            <div className="grid grid-cols-3 gap-2">
              {p.photos.map((ph) => (
                <div key={ph.photo_id} className="text-[10px] text-slate-400">
                  <div className="h-16 bg-slate-100 rounded flex items-center justify-center">img</div>
                  {(ph.taken_at || "").slice(0, 10)}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Full photographs are included in the PDF package.</p>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 border border-slate-100" data-testid="evidence-share-semantics">
          <div className="text-sm font-medium text-[#051329] mb-2">Interpretation boundaries</div>
          <ul className="space-y-1 text-xs text-slate-500">{(p.semantics || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
          <p className="text-[11px] text-slate-400 mt-3">{p.disclaimer}</p>
        </div>
      </div>
    </div>
  );
}
